import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import eventService from "../services/eventService";
import { format } from "date-fns-tz";
import { es } from "date-fns/locale";
import SpinnerOverlay from "./SpinnerOverlay";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaCalendarAlt } from "react-icons/fa";
import { FaRegClock } from "react-icons/fa";
import { FaChevronRight } from "react-icons/fa";
import NotFound from "./NotFoundEvent";
import { MdOutlineWork } from "react-icons/md";
import { slugifyProducerName } from "../services/producerService";

/* eslint-disable react/prop-types */
const EventDetailPage = ({ setStatus, onEventLoaded }) => {
    // Obtener el slug del evento desde la URL
    const { slug } = useParams();

    // Estado para almacenar los detalles del evento
    const [event, setEvent] = useState(null);
    const [producer, setProducer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Función para obtener los detalles del evento
    useEffect(() => {
        const fetchEventDetails = async () => {
            try {
                const eventData = await eventService.getEventForSlug(slug);
                const event = eventData.event;
                setProducer(eventData.producer);
                const [year, month, day] = event.fecha.split("-");
                const localDate = new Date(year, month - 1, day); // Meses en JS son 0-based (0 = Enero)
                const normalizedEvent = { ...event, fecha: localDate };
                setEvent(normalizedEvent);
                onEventLoaded?.(normalizedEvent);
                setStatus(event.estado);
            } catch (err) {
                console.log(err);
                setError("Error al cargar los detalles del evento." + err);
            } finally {
                setLoading(false);
            }
        };

        fetchEventDetails();
    }, [slug, onEventLoaded, setStatus]);

    // Inserta codigo de meta
    useEffect(() => {

        if (!producer?.metaPixelId) return;

        // Verificar si ya cargamos el script de Meta
        if (!window.fbq) {
            (function (f, b, e, v, n, t, s) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod
                        ? n.callMethod.apply(n, arguments)
                        : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = true;
                n.version = "2.0";
                n.queue = [];
                t = b.createElement(e);
                t.async = true;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t, s);
            })(
                window,
                document,
                "script",
                "https://connect.facebook.net/en_US/fbevents.js"
            );
        }

        // Inicializar el pixel del productor
        window.fbq("init", producer.metaPixelId);
        window.fbq("track", "PageView");

    }, [producer]);

    // Mostrar mensaje de carga mientras se obtiene el evento
    if (loading) {
        return <SpinnerOverlay isLoading={loading} />;
    }

    // Mostrar mensaje de error si ocurre un problema
    if (error) {
        return <NotFound />;
    }
    return (
        <div>
            <div
                className="hiden max-[1030px]:block absolute top-0 right-0 -z-10 bg-cover bg-center blur-xl scale-110 h-[500px] w-full opacity-50  "
                style={{ backgroundImage: `url(${event.img})` }}
            >
               
            </div>
            <div className="flex flex-wrap justify-center py-10 gap-5 ">
                <div>
                    <img
                        src={event?.img}
                        alt={event?.nombre}
                        className="w-full object-cover rounded-md sm:max-h-[400px] "
                    />
                </div>
                <div className="p-4 lg:w-auto w-full max-w-2xl">
                    {/* Titulo del evento */}
                    <h1 className="text-5xl font-bold text-zinc-300 mb-6 uppercase">
                        {event?.nombre}
                    </h1>
                    <div className="mb-4 sapce-y-3">
                        {/* Fecha del evento */}
                        <div className="flex gap-2 items-baseline p-2">
                            <div>
                                <FaCalendarAlt className="text-zinc-500" />
                            </div>
                            <div>
                                <p className="text-zinc-500">Fecha</p>
                                <p className="text-zinc-300">
                                    {format(
                                        event?.fecha,
                                        "EEEE, d 'de' MMMM 'de' yyyy",
                                        {
                                            locale: es,
                                            timeZone:
                                                "America/Argentina/Buenos_Aires",
                                        }
                                    )}
                                </p>
                            </div>
                        </div>
                        {/*Hora del evento */}
                        {event.hora && (
                            <div className="flex items-baseline gap-2 p-2">
                                <div>
                                    <FaRegClock className="text-zinc-500" />
                                </div>
                                <div>
                                    <p className="text-zinc-500">Hora</p>
                                    <p className="text-zinc-100">
                                        {event?.hora}
                                    </p>
                                </div>
                            </div>
                        )}
                        {/* Ubicación del evento */}
                        {event?.ubicacion && (
                            <div className="flex items-baseline gap-2 p-2">
                                <div>
                                    <FaMapMarkerAlt className="text-zinc-500" />
                                </div>
                                <div>
                                    <p className="text-zinc-500">Ubicación</p>
                                    <a
                                        className="text-zinc-300 "
                                        href={`https://www.google.com/maps/search/?api=1&query=${event?.Location?.address}`}
                                    >
                                        {event?.ubicacion} -{" "}
                                        {event?.Location?.address}
                                    </a>
                                </div>
                            </div>
                        )}
                        {/* Productor */}
                        {producer?.name && (
                            <div className="p-2">
                                <Link
                                    to={`/producers/${slugifyProducerName(
                                        producer.name
                                    )}`}
                                    className="group mt-2 block max-w-md"
                                >
                                    <div className="flex items-center gap-3 rounded-2xl border border-zinc-800 bg-zinc-900/80 p-3 transition duration-300 hover:-translate-y-0.5 hover:border-zinc-600 hover:bg-zinc-900">
                                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-zinc-700 bg-zinc-800">
                                            <img
                                                src={producer?.img || "/avatar.jpg"}
                                                alt={producer?.name || "Productora"}
                                                className="h-full w-full object-cover"
                                            />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                                                <MdOutlineWork className="text-sm" />
                                                <span>Productora</span>
                                            </div>
                                            <p className="truncate text-base font-semibold text-zinc-100">
                                                {producer?.name}
                                            </p>
                                            <p className="text-sm text-zinc-400">
                                                Ver perfil
                                            </p>
                                        </div>

                                        <FaChevronRight className="shrink-0 text-zinc-500 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-zinc-200" />
                                    </div>
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Descripción del evento */}
                    <div className="text-zinc-400 leading-relaxed">
                        <p>{event?.descripcion}</p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default EventDetailPage;
